import { useEffect, useRef } from 'react';

import { useAuthContext } from '@/context/AuthProvider';
import { useWebSocketContext } from '@/context/WebSocketProvider';
import { useUserProfile } from '@/hooks/user/useUserProfile';

const activeChatViews = new Set<string>();

export const registerChatView = (id: string) => {
  activeChatViews.add(id);
};

export const unregisterChatView = (id: string) => {
  activeChatViews.delete(id);
};

const playChime = () => {
  try {
    const ctx = new AudioContext();
    const now = ctx.currentTime;

    // First tone
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.value = 880;
    gain1.gain.setValueAtTime(0.15, now);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start(now);
    osc1.stop(now + 0.15);

    // Second tone (higher, slight delay)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.value = 1175;
    gain2.gain.setValueAtTime(0.12, now + 0.1);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(now + 0.1);
    osc2.stop(now + 0.25);

    // Clean up
    setTimeout(() => ctx.close(), 500);
  } catch {
    // AudioContext not available
  }
};

export const useChatNotifications = () => {
  const { user } = useAuthContext();
  const { lastGroupChatMessage } = useWebSocketContext();
  const { userProfile } = useUserProfile();
  const lastProcessedGroupRef = useRef<string | null>(null);

  const notificationFrequency = userProfile?.notificationFrequency ?? 'direct';

  useEffect(() => {
    if (!lastGroupChatMessage || !user) return;
    if (notificationFrequency === 'none') return;

    const { data: message, groupId } = lastGroupChatMessage;

    if (lastProcessedGroupRef.current === message.messageId) return;
    lastProcessedGroupRef.current = message.messageId;

    if (message.userId === user.id) return;
    if (activeChatViews.has(groupId)) return;

    playChime();
  }, [lastGroupChatMessage, user, notificationFrequency]);
};
